type Constructor<Instance> = { prototype: Instance };
type TypeGuard<Instance> = (value: unknown) => value is Instance;
type Matcher<Instance> = Constructor<Instance> | TypeGuard<Instance>;

type Mapped<T> =
    T extends TypeGuard<infer Instance>
        ? Instance
        : T extends Constructor<infer Instance>
          ? Instance
          : never;

type RegisteredMapper<T> = {
    from: Matcher<unknown>;
    map: (value: unknown) => T;
};

export default class Caster<T> {
    private readonly mappers: RegisteredMapper<T>[] = [];

    constructor(private readonly fallback: T) {}

    register<From extends Matcher<unknown>>(from: From, map: (value: Mapped<From>) => T) {
        this.mappers.push({ from, map: map as (value: unknown) => T });
    }

    cast(value: unknown): T {
        const mapper = this.findMapper(value);
        if (mapper) return mapper.map(value);
        return this.fallback;
    }

    handles(value: unknown): boolean {
        return this.findMapper(value) !== undefined;
    }

    private findMapper(value: unknown) {
        return this.mappers.find((mapper) => matches(mapper.from, value));
    }
}

function matches(from: Matcher<unknown>, value: unknown): boolean {
    if (isTypeGuard(from)) return from(value);
    return value instanceof (from as new (...args: never[]) => unknown);
}

function isTypeGuard(from: Matcher<unknown>): from is TypeGuard<unknown> {
    return (
        typeof from === "function" &&
        Object.getOwnPropertyDescriptor(from, "prototype")?.writable !== false
    );
}
